/**
 * useTestEnvWarning Hook
 *
 * Displays a warning notification once per session in non-production environments.
 * Uses sessionStorage to track whether the warning has already been shown.
 * Graceful fallback if sessionStorage is unavailable (shows warning every time).
 *
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.5, 8.6
 */

import { useEffect } from 'react';
import { useNotification } from '../contexts/NotificationContext';

import { PROJECT_PREFIX } from '../config/project';

const SESSION_KEY = `${PROJECT_PREFIX}-test-env-warning-shown`;
const WARNING_MESSAGE =
    'Test Environment - This is a testing environment. Data may be automatically deleted based on the configured TTL policy.';
const AUTO_CLOSE_DURATION = 8000;

export function useTestEnvWarning(): void {
    const { notify } = useNotification();

    useEffect(() => {
        // Skip in production environments
        if (import.meta.env.PROD) {
            return;
        }

        // Check sessionStorage for once-per-session behavior
        let alreadyShown = false;
        try {
            alreadyShown = sessionStorage.getItem(SESSION_KEY) === 'true';
        } catch {
            // sessionStorage unavailable (e.g. private browsing) — show warning every time
        }

        if (alreadyShown) {
            return;
        }

        // Small delay so the notification appears after the initial page render settles
        const timer = setTimeout(() => {
            notify({
                message: WARNING_MESSAGE,
                severity: 'warning',
                autoCloseDuration: AUTO_CLOSE_DURATION,
            });

            try {
                sessionStorage.setItem(SESSION_KEY, 'true');
            } catch {
                // Ignore — graceful degradation
            }
        }, 800);

        return () => clearTimeout(timer);
    }, [notify]);
}
